import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { readStashedUTM, clearStashedUTM } from "@/lib/utm";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const signup = useCallback(async (name, email, password, shareToken) => {
    // Phase 23 — if the user landed via a referral link, the share_token is
    // forwarded as a query param so the backend can grant Pro to both users.
    // Phase 36 Batch T — also forward any captured UTM tags so the signup
    // event in user_events carries acquisition attribution.
    const utm = readStashedUTM();
    let params;
    if (shareToken) {
      // Referral path wins over generic UTM (we still attach the other UTM
      // fields for attribution but force utm_source=referral).
      params = { ...utm, utm_source: "referral", share_token: shareToken };
    } else if (Object.keys(utm).length > 0) {
      params = utm;
    }
    const { data } = await api.post("/auth/signup", { name, email, password }, { params });
    // UTM stash is one-shot — once it's been attributed to a signup we
    // remove it so a returning visitor on the same session doesn't double-
    // attribute their second signup attempt.
    clearStashedUTM();
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Even on failure, clear local state — but surface in dev to catch CSRF/CORS regressions.
      if (process.env.NODE_ENV !== "production") {
        console.warn("AuthContext.logout API call failed; clearing local state anyway", err);
      }
    }
    setUser(null);
  }, []);

  const value = { user, loading, login, signup, logout, refresh, isAuthenticated: !!user };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
